import React from "react";
import { Relative } from "../../BuildingBlocks/Relative";
import { Absolute } from "../../BuildingBlocks/Absolute";
import { useOpenClose } from "../../../hooks/UseOpenClose";
import { useHover } from "../../../hooks/useHover";
import { CRMColors } from "../../../models/CRMColors";
import { CRMZIndex } from "../../../models/CRMZIndex";
import { CursorPointer } from "../../CursorPointer/CursorPointer";
import { CRMIconAvatar } from "../../Complex/CRMIconAvatar/CRMIconAvatar";
import { AvatarSize } from "../../Complex/CRMUserAvatar/CRMUserAvatar";
import { TRoadblockAwaitableEntity } from "../../../../../domain/codecs/form/RoadblockForm";
import { CRMRoadblockAwaitPopout } from "../CRMRoadblockAwaitPopout/CRMRoadblockAwaitPopout";
import { getPersistedAwaitRecentEntityIds, pushPersistedAwaitRecentEntityId } from "../../../state/actions/awaitRecentEntitiesPersistence";

type TCRMRoadblockAwaitAvatarProps = {
    size: keyof typeof AvatarSize;
    entities: Array<TRoadblockAwaitableEntity>;
    activeAwait: { waiting_on: string; blocked_till: string; is_internal: boolean } | null;
    onAwait: (awaitableEntityId: string, days: number | null) => void;
    onCancelAwait: () => void;
    onToggleOpen?: (isOpen: boolean) => void;
};

// The quick-await twin of CRMAssignUserAvatar: a chip that opens the shared
// await popout to defer the roadblock (#await) or cancel its active await.
export const CRMRoadblockAwaitAvatar = (props: TCRMRoadblockAwaitAvatarProps): JSX.Element => {

    // A persistent ref (useOpenClose's own ref is recreated every render, so its
    // .current is always null mid-render and can't be measured).
    const chipRef = React.useRef<HTMLDivElement>(null);
    const {
        ref,
        isOpen,
        toggleOpen,
        setIsOpen,
    } = useOpenClose({
        inputRef: chipRef,
        onToggle: props.onToggleOpen,
    });

    const [hoverRef, isHovering] = useHover<HTMLDivElement>({});

    const hasBorder = isHovering || isOpen;
    const hasActiveAwait = props.activeAwait !== null;
    // Everything except the scrollable party list: title, search box, any active-await row,
    // the duration stepper and the keyboard hint.
    const popoutChromeHeight = 180;
    const listMaxHeight = 240;
    const listMinHeight = 120;
    const estimatedPopoutHeight = popoutChromeHeight + listMaxHeight;

    // Whichever scroll container the chip happens to sit in is what clips the popout, so the
    // popout gets cut off at *either* of its edges — the bottom on the last rows, and the top on
    // the first rows. Two containers use this control: the case-view block body
    // (.crm-block-view) and the roadblock board's table body (.crm-table-sheet__body). Matching
    // only the first made the board measure against the viewport, conclude it had room, open
    // upward and then get clipped — hiding "Cancel await" on the top rows. Falls back to the
    // viewport when there is no such ancestor.
    const getSpace = (): { above: number, below: number } => {
        if (chipRef.current === null) {
            return { above: 0, below: estimatedPopoutHeight };
        }
        const chipRect = chipRef.current.getBoundingClientRect();
        const containerRect = chipRef.current
            .closest(".crm-block-view, .crm-table-sheet__body")
            ?.getBoundingClientRect();
        const clipTop = containerRect?.top ?? 0;
        const clipBottom = containerRect?.bottom ?? window.innerHeight;
        return {
            above: chipRect.top - clipTop,
            below: clipBottom - chipRect.bottom,
        };
    };

    // Prefer the side with room; when neither fits, take the roomier one so the popout loses as
    // little as possible.
    const space = getSpace();
    const opensUpward =
        space.below >= estimatedPopoutHeight ? false
        : space.above >= estimatedPopoutHeight ? true
        : space.above > space.below;

    // Shrink the list to whatever is actually available on the chosen side. Previously the popout
    // kept its full height and simply overflowed the block's scroll container, clipping the search
    // box and cancel action off the top with no way to scroll back to them — only the list itself
    // scrolled. Fifteen parties made that reachable-content bug a routine one rather than an edge case.
    const availableHeight = (opensUpward ? space.above : space.below) - 12;
    const listMaxHeightPx = Math.round(
        Math.max(listMinHeight, Math.min(listMaxHeight, availableHeight - popoutChromeHeight)),
    );
    const popoutTop = opensUpward ? undefined : `calc(${AvatarSize[props.size]} + 3px)`;
    const popoutBottom = opensUpward ? `calc(${AvatarSize[props.size]} + 3px)` : undefined;
    // useOpenClose only fires onToggle for its own toggle/click-outside paths, so
    // closing from here has to report it or the parent is left thinking we're open.
    const close = () => {
        setIsOpen(false);
        props.onToggleOpen?.(false);
    };

    const onPick = (awaitableEntityId: string, days: number | null) => {
        pushPersistedAwaitRecentEntityId(awaitableEntityId);
        props.onAwait(awaitableEntityId, days);
        close();
    };

    const onCancel = () => {
        props.onCancelAwait();
        close();
    };

    return (
        <Relative
            divRef={ref}
            width="fit-content"
            // Every row's chip sits at SECOND_Z, so an overlap between two rows is
            // resolved by DOM order and the popout lands under the chips of the rows
            // below it. Lift the row that's showing a popout clear of the rest.
            zIndex={isOpen ? CRMZIndex.FOREMOST_Z : CRMZIndex.SECOND_Z}
        >
            {/* CHIP */}
            <CursorPointer
                divRef={hoverRef}
                onClick={toggleOpen}
            >
                <CRMIconAvatar
                    size={props.size}
                    icon="pause"
                    iconColor={hasActiveAwait ? CRMColors.HIGHLIGHTS_CALMING_PINK_0 : CRMColors.NEUTRAL_2}
                    backgroundColor={hasActiveAwait ? CRMColors.HIGHLIGHTS_CALMING_PINK_10 : CRMColors.NEUTRAL_10}
                    hasBorder={hasBorder}
                />
            </CursorPointer>

            {/* POPOUT — right-aligned to the chip. This used to hardcode the popout's own width
                to compute a negative left offset, so widening the popout in the stylesheet
                silently misaligned it and let the duration row overlap the last party row. */}
            {isOpen &&
                <Absolute
                    top={popoutTop}
                    bottom={popoutBottom}
                    right="0px"
                >
                    <CRMRoadblockAwaitPopout
                        entities={props.entities}
                        activeAwait={props.activeAwait}
                        recentEntityIds={getPersistedAwaitRecentEntityIds()}
                        bulkCount={null}
                        listMaxHeightPx={listMaxHeightPx}
                        onPick={onPick}
                        onCancel={onCancel}
                        onRequestClose={close}
                    />
                </Absolute>
            }
        </Relative>
    );
};
